import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, WarningCircle, Envelope } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState('checking'); // checking | success | error
  const [message, setMessage] = useState('');
  const calledRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setState('error');
      setMessage('No verification token provided. Please use the link from your email.');
      return;
    }
    // Guard against React StrictMode double-invocation which would consume the
    // one-time verification token twice and show a false "failed" state.
    if (calledRef.current) return;
    calledRef.current = true;
    (async () => {
      try {
        const { data } = await axios.post(`${API}/api/auth/verify-email`, { token });
        setMessage(data.message || 'Your email has been verified.');
        setState('success');
      } catch (err) {
        setMessage(err.response?.data?.detail || 'Verification failed. Please request a new link.');
        setState('error');
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full bg-card border border-border rounded-lg p-10 text-center shadow-sm" data-testid="verify-email-container">
        {state === 'checking' && (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-6">
              <Envelope size={40} weight="duotone" className="text-muted-foreground" />
            </div>
            <h2 className="text-3xl mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
              Verifying your email…
            </h2>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mt-4" />
          </>
        )}
        {state === 'success' && (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-950/30 mb-6">
              <CheckCircle size={40} weight="duotone" className="text-green-600" />
            </div>
            <h2 className="text-3xl mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
              Email verified!
            </h2>
            <p className="text-muted-foreground text-sm mb-6" data-testid="verify-success-message">{message}</p>
            <Link
              to="/login"
              data-testid="verify-goto-login"
              className="inline-block bg-primary text-primary-foreground rounded-full px-6 py-3 font-medium hover:opacity-90"
            >
              Sign in now
            </Link>
          </>
        )}
        {state === 'error' && (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-950/30 mb-6">
              <WarningCircle size={40} weight="duotone" className="text-red-600" />
            </div>
            <h2 className="text-3xl mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
              Verification failed
            </h2>
            <p className="text-muted-foreground text-sm mb-6" data-testid="verify-error-message">{message}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/forgot-password" className="text-sm text-primary hover:underline">Request a new link</Link>
              <span className="text-muted-foreground text-sm hidden sm:inline">·</span>
              <Link to="/login" className="text-sm text-primary hover:underline">Back to login</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
